import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Check, X, Pause } from 'lucide-react';
import { CoverImage } from '@/components/CoverImage';
import { Link, useNavigate } from 'react-router-dom';

interface Book {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  cover_cache_url?: string;
  current_page?: number;
  total_pages?: number;
  reading_progress_percent?: number;
  started_at?: string;
}

interface CurrentlyReadingShowcaseProps {
  book: Book;
  isOwnProfile: boolean;
  onUpdateProgress?: () => void;
  onMarkAsRead?: () => void;
  onMarkAsUnfinished?: () => void;
  onPauseReading?: () => void;
  noCard?: boolean;
}

export function CurrentlyReadingShowcase({
  book,
  isOwnProfile,
  onUpdateProgress,
  onMarkAsRead,
  onMarkAsUnfinished,
  onPauseReading,
  noCard = false,
}: CurrentlyReadingShowcaseProps) {
  const navigate = useNavigate();
  const progress = book.reading_progress_percent || 0;
  const startedDate = book.started_at ? new Date(book.started_at).toLocaleDateString() : null;

  const content = (
    <div className="flex items-start gap-3 sm:gap-6">
      {/* Book Cover */}
      <Link to={`/book/${book.id}`} className="shrink-0 group">
        <div className="relative w-20 h-[120px] sm:w-32 sm:h-48 rounded-lg overflow-hidden shadow-lg ring-2 ring-primary/20 transition-all duration-300 group-hover:ring-primary/40 group-hover:shadow-xl">
          <CoverImage
            coverUrl={book.cover_cache_url || book.cover_url}
            title={book.title}
            className="w-full h-full object-cover"
          />
        </div>
      </Link>

      {/* Book Info & Progress */}
      <div className="flex-1 min-w-0 space-y-2 sm:space-y-4">
        <div>
          <Link to={`/book/${book.id}`} className="group">
            <h3 className="text-base sm:text-2xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-tight">
              {book.title}
            </h3>
          </Link>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            by {book.authors.map((author, index) => (
              <span key={author}>
                <a
                  href={`/author/${encodeURIComponent(author)}`}
                  className="hover:text-primary hover:underline transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigate(`/author/${encodeURIComponent(author)}`);
                  }}
                >
                  {author}
                </a>
                {index < book.authors.length - 1 && ', '}
              </span>
            ))}
          </p>
          {startedDate && (
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
              Started {startedDate}
            </p>
          )}
        </div>

        {/* Progress Section - compact on mobile */}
        <div className="space-y-1 sm:space-y-2">
          <div className="flex items-center justify-between text-xs sm:text-sm">
            <span className="text-muted-foreground">
              {book.current_page || 0} / {book.total_pages || '?'} pages
            </span>
            <span className="font-semibold text-primary">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2 sm:h-3" />
        </div>

        {/* Quick Actions - inline row on mobile */}
        {isOwnProfile && (
          <div className="flex items-center gap-1.5 sm:gap-2 pt-1 sm:pt-2 flex-wrap">
            <Button 
              onClick={onUpdateProgress}
              size="sm"
              variant="default"
              className="h-7 sm:h-9 text-xs sm:text-sm px-2.5 sm:px-3 gap-1 sm:gap-2"
            >
              <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
              Update
            </Button>
            <Button 
              onClick={onMarkAsRead}
              size="sm"
              variant="outline"
              className="h-7 sm:h-9 text-xs sm:text-sm px-2.5 sm:px-3 gap-1 sm:gap-2"
            >
              <Check className="h-3 w-3 sm:h-4 sm:w-4" />
              Done
            </Button>
            <Button 
              onClick={onPauseReading}
              size="sm"
              variant="ghost"
              className="h-7 sm:h-9 text-xs sm:text-sm px-2 sm:px-3 gap-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            >
              <Pause className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
            <Button 
              onClick={onMarkAsUnfinished}
              size="sm"
              variant="ghost"
              className="h-7 sm:h-9 text-xs sm:text-sm px-2 sm:px-3 gap-1"
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
      <CardContent className="p-6">
        {content}
      </CardContent>
    </Card>
  );
}
